/**
 * Scenario interpolation logic.
 *
 * Scenario DATA lives in src/data/scenarios.js (single medical-reference source,
 * validated by `npm run check:medical`). This module re-exports it for backward
 * compatibility and holds only the transition/interpolation logic.
 */
import { SCENARIOS, BASELINE_VENT } from '../data/scenarios';

export { SCENARIOS, BASELINE_VENT };

// The interpolation loop ticks at this interval (ms). Exported so the caller's
// setInterval and the per-tick step math stay in lock-step.
export const TRANSITION_TICK_MS = 50;

/**
 * Interpolate between current and target values
 * Returns a number between current and target based on progress (0-1)
 */
export function interpolateValue(current, target, progress) {
  const p = Math.max(0, Math.min(1, progress));   // never overshoot the target
  return current + (target - current) * p;
}

/**
 * Advance all in-flight vital transitions one tick toward their targets.
 *
 * Constant-velocity (linear) in REAL TIME: a vital travels its full
 * current→target distance in exactly `currentTransitionDuration` ms, so the
 * instructor's "45 s" really means 45 s of wall-clock ramp.
 *
 * How: each tick we move a fraction `sinceLastTick / remainingTime` of the
 * *remaining* distance. That fraction grows as the deadline approaches such that
 * the distance-per-time (velocity) stays constant — covering distance D over the
 * remaining time T in steps of real size Δt, since D·(Δt/T) leaves D'=D(1−Δt/T)
 * and T'=T−Δt with D'/T' = D/T. This also self-corrects if the target or a new
 * drug is applied mid-transition (it re-aims from wherever the value is now).
 *
 * CRITICAL: the step uses the ACTUAL wall-clock time since the previous tick
 * (`sinceLastTick`), not the nominal timer interval. Browsers throttle
 * background/blurred tabs' setInterval to ~1 s, so on the real two-window /
 * two-device setup the instructor panel (usually not the focused window) ticks
 * far slower than its nominal 50 ms. If we stepped by the nominal 50 ms, each
 * tick would advance only 50 ms of distance while ~1 s of real time elapsed —
 * the value would crawl for most of the countdown and then snap near the end.
 * Sizing the step by real Δt keeps the ramp even regardless of tick cadence.
 *
 * `elapsed` = time since the transition started; `remaining = duration − elapsed`.
 */
export function updateTransition(store, elapsed, sinceLastTick = TRANSITION_TICK_MS) {
  const { vitals, currentTransitionDuration, setVitalCurrent, setIsTransitioning } = store;
  const transitionDuration = currentTransitionDuration;

  const remaining = transitionDuration - elapsed;

  // Immediate, or the deadline is within the next real step: snap to target.
  if (!transitionDuration || remaining <= sinceLastTick) {
    Object.entries(vitals).forEach(([vitalName, vital]) => {
      if (vital.current !== vital.target) {
        setVitalCurrent(vitalName, vital.target);
      }
    });
    setIsTransitioning(false);
    return;
  }

  // Fraction of the REMAINING distance to cover for the REAL time elapsed since
  // the last tick → constant velocity even when the timer is throttled.
  const stepFrac = Math.min(sinceLastTick / remaining, 1);
  let isStillTransitioning = false;

  Object.entries(vitals).forEach(([vitalName, vital]) => {
    if (vital.current !== vital.target) {
      setVitalCurrent(vitalName, vital.current + (vital.target - vital.current) * stepFrac);
      isStillTransitioning = true;
    }
  });

  setIsTransitioning(isStillTransitioning);
}
